import { useEventManager } from '@deathbyjer/react-event-manager'
import { setSelected } from 'components/form_builder/features/components/componentSlice'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const createDragElement = ({ x, y }) => {
  const div = document.createElement('div')
  div.className = 'drag-to-select'
  div.style.left = `${x}px`
  div.style.top = `${y}px`
  div.style.width = '1px'
  div.style.height = '1px'
  div.style.border = '1px solid #0000DD'
  div.style.backgroundColor = '#99ccff'
  div.style.opacity = '0.3'
  div.style.zIndex = '9999'
  div.style.position = 'absolute'
  div.style.pointerEvents = 'none'
  return div
}

const clearPreviousDragElements = () => {
  const previousElements = document.getElementsByClassName('drag-to-select')
  Array.from(previousElements).forEach((el) => el.remove())
}

const updateDragElementStyle = (element, startPos, curPos) => {
  const deltaX = curPos.x - startPos.x
  const deltaY = curPos.y - startPos.y
  const width = Math.abs(deltaX)
  const height = Math.abs(deltaY)
  const translateX = deltaX < 0 ? -width : 0
  const translateY = deltaY < 0 ? -height : 0

  element.style.width = `${width}px`
  element.style.height = `${height}px`
  element.style.transform = `translate(${translateX}px, ${translateY}px)`
}

const getComponentsInDragArea = ({ components, selectionBox }) => {
  return components.filter((component) => {
    const pageElement = document.getElementById(`page-${component.page}`)
    if (!pageElement) return false

    const pageOffsetTop = pageElement.offsetTop
    const pageHeight = pageElement.clientHeight
    const pageWidth = pageElement.clientWidth

    const compStartX = (component.rect.x / 100) * pageWidth
    const compEndX = compStartX + (component.rect.width / 100) * pageWidth
    const compStartY = (component.rect.y / 100) * pageHeight + pageOffsetTop
    const compEndY = compStartY + (component.rect.height / 100) * pageHeight

    const overLapsLeftX = compStartX >= selectionBox.startX && compStartX <= selectionBox.endX
    const overLapsLeftY =
      (compStartY >= selectionBox.startY && compStartY <= selectionBox.endY) ||
      (compEndY >= selectionBox.startY && compEndY <= selectionBox.endY) ||
      (compStartY <= selectionBox.startY && compEndY >= selectionBox.endY)

    const overLapsRightX = compEndX >= selectionBox.startX && compEndX <= selectionBox.endX
    const overLapsRightY =
      (compStartY >= selectionBox.startY && compStartY <= selectionBox.endY) ||
      (compEndY >= selectionBox.startY && compEndY <= selectionBox.endY) ||
      (compStartY <= selectionBox.startY && compEndY >= selectionBox.endY)

    const overLapsTopX = compStartX <= selectionBox.startX && compEndX >= selectionBox.endX
    const overLapsTopY = compStartY >= selectionBox.startY && compStartY <= selectionBox.endY

    const overLapsBottomX = compStartX <= selectionBox.startX && compEndX >= selectionBox.endX
    const overLapsBottomY = compEndY >= selectionBox.startY && compEndY <= selectionBox.endY
    return (
      (overLapsLeftX && overLapsLeftY) ||
      (overLapsRightX && overLapsRightY) ||
      (overLapsTopX && overLapsTopY) ||
      (overLapsBottomX && overLapsBottomY)
    )
  })
}

const ensureIdIsString = (id) => (Array.isArray(id) ? id.join('.') : id)

const getSymmetricDifference = (preArr, curArr) => {
  const map = new Map()

  for (const item of [...preArr, ...curArr]) {
    const key = ensureIdIsString(item)
    if (map.has(key)) {
      map.delete(key) // cancel out if seen twice
    } else {
      map.set(key, item)
    }
  }

  return [...map.values()]
}

export default function useDragToSelect({ scrollRef, containerRef, enabled = false }) {
  const events = useEventManager()
  const dispatch = useDispatch()

  const isHoldingShift = useRef(false)
  const dragElementRef = useRef(null)
  const startPosRef = useRef(null)
  const layoutRef = useRef(null)

  const allComponents = useSelector((state) => state.components.all)
  const selectedComponents = useSelector((state) => state.components.selected)

  const previousSelectedIdsRef = useRef([])
  const currentSelectedIdsRef = useRef([])

  const handleComponentsInDragArea = useCallback(
    (selectionBox) => {
      const components = Object.values(allComponents)
      const componentsToSelect = getComponentsInDragArea({ components, selectionBox })
      let ids = componentsToSelect.map((c) => c.key.split('.'))
      ids = isHoldingShift.current ? getSymmetricDifference(previousSelectedIdsRef.current, ids) : ids

      currentSelectedIdsRef.current = ids
      events.applyEventListeners('selectMultipleComponentsByDragging', ids)
    },
    [allComponents, events]
  )

  const updateSelectionBox = useCallback(() => {
    if (!containerRef.current) return
    const containerRect = containerRef.current.getBoundingClientRect()
    const x = layoutRef.current.x - containerRect.left
    const y = layoutRef.current.y - containerRect.top
    const currentPos = { x, y }

    updateDragElementStyle(dragElementRef.current, startPosRef.current, currentPos)

    const selectionBox = {
      startX: Math.min(startPosRef.current.x, currentPos.x),
      startY: Math.min(startPosRef.current.y, currentPos.y),
      endX: Math.max(startPosRef.current.x, currentPos.x),
      endY: Math.max(startPosRef.current.y, currentPos.y),
    }

    handleComponentsInDragArea(selectionBox)
  }, [containerRef, handleComponentsInDragArea])

  const onMouseDown = useCallback(
    (event) => {
      clearPreviousDragElements()
      currentSelectedIdsRef.current = []

      // We only start dragging to select when the mouse is in an empty position.
      if (!event.target.className.includes('draw-area-component')) return
      if (!enabled || !containerRef.current) return
      if (isHoldingShift.current) previousSelectedIdsRef.current = selectedComponents.map((c) => c.split('.'))

      layoutRef.current = { x: event.clientX, y: event.clientY }

      const containerRect = containerRef.current.getBoundingClientRect()
      const x = layoutRef.current.x - containerRect.left
      const y = layoutRef.current.y - containerRect.top
      startPosRef.current = { x, y }

      const dragElement = createDragElement(startPosRef.current)
      containerRef.current.appendChild(dragElement)
      dragElementRef.current = dragElement
    },
    [enabled, containerRef, selectedComponents]
  )

  const onMouseMove = useCallback(
    (event) => {
      if (!startPosRef.current || !dragElementRef.current || !containerRef.current) return
      layoutRef.current = { x: event.clientX, y: event.clientY }
      updateSelectionBox()
    },
    [containerRef, updateSelectionBox]
  )

  const onMouseUp = useCallback(() => {
    if (dragElementRef.current) containerRef.current.removeChild(dragElementRef.current)
    if (currentSelectedIdsRef.current.length) dispatch(setSelected(currentSelectedIdsRef.current))
    previousSelectedIdsRef.current = currentSelectedIdsRef.current
    layoutRef.current = null
    startPosRef.current = null
    dragElementRef.current = null
  }, [containerRef])

  // handle scrolling
  useEffect(() => {
    const scrollEl = scrollRef?.current
    if (!scrollEl) return

    const handleScroll = (event) => {
      if (startPosRef.current) updateSelectionBox()
    }

    scrollEl.addEventListener('scroll', handleScroll)
    return () => {
      if (scrollEl) scrollEl.removeEventListener('scroll', handleScroll)
    }
  }, [scrollRef, updateSelectionBox])

  // handle continue selection if user holding shift
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.shiftKey) isHoldingShift.current = true
    }
    const handleKeyUp = (event) => {
      isHoldingShift.current = false
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('mouseup', onMouseUp) // in case mouse is released outside of the container

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  return { onMouseDown, onMouseUp, onMouseMove }
}
